import { isV2ClaimSupported } from "./v2/claim-support";
import { fetchIrisMessages, type IrisMessage } from "./v2/iris";
import { parseCCTPMessageV2 } from "./v2/message";

/**
 * Fetch a CCTP message from Circle's API by domain ID and tx hash.
 *
 * Polls the v2 endpoint: it serves v1 and v2 transfers alike, while the v1 endpoint 404s on v2.
 * A v2 transfer is returned only once the app can claim its route (`isV2ClaimSupported`).
 *
 * - Returns the first message Circle has observed for the transaction
 * - Returns `null` when Circle hasn't seen it yet (404 or an empty list) or it's a hidden v2 transfer
 * - Throws `CCTPProxyError` on any other non-ok response so callers can distinguish outage
 *   from not-found (resume modals surface "api_error", polling logs to Sentry)
 */
export async function fetchCCTPMessage({
  domainId,
  txHash,
}: {
  domainId: number;
  txHash: string;
}): Promise<IrisMessage | null> {
  const messages = await fetchIrisMessages({ domainId, txHash });
  return (
    messages?.find(
      (m) =>
        m.cctpVersion === 1 ||
        isV2ClaimSupported({
          sourceDomain: domainId,
          destDomain: m.message ? (parseCCTPMessageV2(m.message)?.destDomain ?? null) : null,
        }),
    ) ?? null
  );
}
